/**
 * The member view of the portal.
 *
 * A plain member signing in on the web used to land in Club Office: every
 * office section in the sidebar, several of which answered 403 behind the
 * link. Nothing leaked — the API gates registrations, office messages, door
 * access and check-in stations, and redacts dates of birth, addresses,
 * emergency contacts and private notes from anybody who is not an
 * administrator — but it read as a broken admin tool and invited a member to
 * poke at screens that were not theirs.
 *
 * So a member gets this instead: the same five things the companion app
 * shows, and nothing more. The screens here deliberately mirror the app's
 * tabs, and they call exactly the endpoints the app calls —
 * `/games/mine/next`, `/duties/mine`, `/draws`, `/standings/:id`,
 * `/content/posts`, `/comms/notifications`, `/messages`, `/waivers/mine`,
 * `/checkin/credential`, `/leagues` — so there is no way for this view to
 * show something the app would not.
 *
 * It reuses the console: its build, its stylesheet, its API client, its auth.
 * What it does not reuse is the office *pages*, because those are shaped
 * around club-wide data and office actions.
 */
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { NextGamePage } from './NextGame';
import { MySchedulePage } from './MySchedule';
import { MyStandingsPage } from './MyStandings';
import { ClubPage } from './Club';
import { MePage } from './Me';
import { SparesPage } from './Spares';

export function MemberApp() {
  return (
    <div className="shell">
      <MemberSidebar />
      <div className="main">
        <Routes>
          <Route path="/" element={<NextGamePage />} />
          <Route path="/schedule" element={<MySchedulePage />} />
          <Route path="/standings" element={<MyStandingsPage />} />
          <Route path="/club" element={<ClubPage />} />
          <Route path="/spares" element={<SparesPage />} />
          <Route path="/me" element={<MePage />} />
          {/*
            Everything else — /members, /money, /registrations, /access and the
            rest of Club Office — is not a page a member has. Sending them to
            their own home is better than a 404 for a URL they were never
            given, and better than a 403 from a screen that should not have
            been reachable.
          */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
  );
}

function MemberSidebar() {
  const { user, club, signOut } = useAuth();

  return (
    <nav className="side" aria-label="Members">
      <a className="side__brand" href="/">
        {club?.logoUrl
          ? <img src={club.logoUrl} alt="" />
          : <img src="/logo.svg" alt="" />}
        <span>
          <b>Members</b>
          <span>{club?.name ?? 'Curling Club'}</span>
        </span>
      </a>

      <div className="side__group">Curling</div>
      <NavLink to="/" className="nav" end>Next game</NavLink>
      <NavLink to="/schedule" className="nav">Schedule</NavLink>
      <NavLink to="/standings" className="nav">Standings</NavLink>
      <NavLink to="/spares" className="nav">Spares</NavLink>

      <div className="side__group">The club</div>
      <NavLink to="/club" className="nav">News &amp; messages</NavLink>
      <NavLink to="/me" className="nav">You</NavLink>

      <div className="side__foot">
        <div className="side__me">
          <b>{user?.fullName}</b>
          <span>Member</span>
        </div>
        <button className="btn btn--sm btn--block" type="button" onClick={signOut}
          style={{ marginTop: '.5rem' }}>
          Sign out
        </button>
      </div>
    </nav>
  );
}
